import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, CreditCard, Mail, Package, Search, UserPlus } from 'lucide-react';
import { authApi, orderApi } from '../api/client';
import { formatTndWithEur } from '../utils/currency';
import './TrackOrderPage.css';

const STATUS_LABELS = {
  draft: 'Brouillon',
  request_sent: 'Demande envoyée',
  pending_review: 'En cours d\'examen',
  quote_sent: 'Devis envoyé',
  quote_accepted: 'Devis accepté',
  payment_pending: 'En attente de paiement',
  paid: 'Payée',
  translator_to_assign: 'Traducteur à assigner',
  translator_assigned: 'Traducteur assigné',
  translation_in_progress: 'Traduction en cours',
  translation_submitted: 'Traduction soumise',
  verification_requested: 'Vérification demandée',
  lawyer_notary_in_progress: 'Vérification en cours',
  correction_requested: 'Correction demandée',
  new_version: 'Nouvelle version',
  verification_validated: 'Vérification validée',
  final_control: 'Contrôle final',
  ready_to_deliver: 'Prête à livrer',
  delivered: 'Livrée',
  completed: 'Terminée',
  refused: 'Refusée',
  cancelled: 'Annulée',
  payment_failed: 'Paiement échoué',
  quote_expired: 'Devis expiré',
  translator_unavailable: 'Traducteur indisponible',
  pending: 'En attente',
  assigned: 'Assignée',
  in_progress: 'En cours',
  ready: 'Prête',
};

export default function TrackOrderPage() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') || '');
  const [token, setToken] = useState(params.get('token') || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [claimSent, setClaimSent] = useState(false);
  const [claimSending, setClaimSending] = useState(false);

  const fetchOrder = async (c, tk) => {
    setLoading(true);
    setError('');
    try {
      const res = await orderApi.track(c, tk);
      setOrder(res.order);
    } catch {
      setOrder(null);
      setError('Commande introuvable. Vérifiez le numéro et le lien sécurisé reçus par email.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const c = params.get('code');
    const tk = params.get('token');
    if (c && tk) fetchOrder(c, tk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await orderApi.pay(order.code, { token, amount: order.price, currency: order.currency, method: 'card', success: true });
      if (res.payment?.status === 'paid') {
        setOrder({ ...order, status: 'paid', paymentStatus: 'paid' });
      }
    } catch (err) {
      setError(
        err?.data?.error === 'translatorNotConfirmed'
          ? 'Le paiement sera disponible dès qu’un professionnel aura accepté votre commande.'
          : 'Erreur lors du paiement. Veuillez réessayer.'
      );
    } finally {
      setPaying(false);
    }
  };

  const handleClaim = async () => {
    const email = order?.contact?.email;
    if (!email || claimSending || claimSent) return;
    setClaimSending(true);
    try {
      await authApi.claim(email);
    } catch {
      // réponse identique quelle que soit l'issue
    } finally {
      setClaimSent(true);
      setClaimSending(false);
    }
  };

  return (
    <div className="track-page">
      <div className="container">
        <div className="track-card">
          <div className="track-icon">
            <Package size={34} />
          </div>
          <h1>Suivi de commande</h1>
          <p className="track-lead">
            Consultez l&apos;état de votre commande grâce au lien sécurisé reçu par email,
            ou saisissez votre numéro de commande et votre code de suivi.
          </p>

          {!order && (
            <form
              className="track-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (code.trim() && token.trim()) fetchOrder(code.trim(), token.trim());
              }}
            >
              <label>
                Numéro de commande
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ORD-1042" required />
              </label>
              <label>
                Code de suivi
                <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Code présent dans le lien de l'email" required />
              </label>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                <Search size={16} /> {loading ? 'Recherche…' : 'Suivre ma commande'}
              </button>
            </form>
          )}

          {error && <p className="track-error" role="alert">{error}</p>}

          {order && (
            <div className="track-result">
              <dl className="track-details">
                <div><dt>Numéro de commande</dt><dd>{order.code}</dd></div>
                <div><dt>Prestation</dt><dd>{order.document}</dd></div>
                <div><dt>Langues</dt><dd>{order.langs}</dd></div>
                <div><dt>Montant</dt><dd>{formatTndWithEur(order.price)}</dd></div>
                <div><dt>Date</dt><dd>{order.date}</dd></div>
                <div><dt>Statut</dt><dd>{STATUS_LABELS[order.status] || order.status}</dd></div>
                <div><dt>Paiement</dt><dd>{order.paymentStatus === 'paid' ? 'Payé' : 'Non réglé'}</dd></div>
              </dl>

              {order.paymentStatus !== 'paid' && !order.paymentReady && (
                <p className="track-note">
                  Le paiement sera disponible dès qu&apos;un professionnel aura accepté votre commande.
                  Vous serez informé par email.
                </p>
              )}

              <div className="track-actions">
                {order.paymentStatus !== 'paid' && order.paymentReady && (
                  <button type="button" className="btn btn-primary" onClick={handlePay} disabled={paying}>
                    <CreditCard size={16} /> {paying ? 'Paiement…' : 'Payer maintenant'}
                  </button>
                )}
                {order.paymentStatus === 'paid' && (
                  <span className="track-paid"><CheckCircle size={16} /> Paiement confirmé</span>
                )}
                {order.guest && !claimSent && (
                  <button type="button" className="btn btn-outline" onClick={handleClaim} disabled={claimSending}>
                    <UserPlus size={16} /> {claimSending ? 'Envoi…' : 'Créer mon compte'}
                  </button>
                )}
                {order.guest && claimSent && (
                  <span className="track-claim-sent"><Mail size={16} /> Email d&apos;activation envoyé à {order.contact?.email}</span>
                )}
              </div>

              {order.guest && (
                <p className="track-note">
                  Créez votre compte pour retrouver toutes vos commandes dans votre espace client.
                </p>
              )}
            </div>
          )}

          <p className="track-foot">
            <Link to="/" className="dash-link">← Retour à l&apos;accueil</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
